import React, { useState, useEffect, useContext, useRef } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import { fetchLocalTimezone, updateReportSchedule } from "../../api/billing";
import Logger from "../../common/utils/Logger";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import { validateEmail } from "../../common/utils/ControllerUtils";
import { parseError } from "@ntconnect/framework/pbx";
import NtEmailItem from "../../components/NtEmailItem";
import NtModalError from "../../components/NtModalError";
import NtTabController from "../../components/NtTabController";

function NtReportScheduleUpdateForm({ item, onCancel, onUpdate }) {
  const timezones = [
    {
      id: 0,
      title: "UTC",
    },
    {
      id: 1,
      title: "Local Time",
    },
  ];

  const [loading, setLoading] = useState(false);
  const [receiver, setReceiver] = useState(item.receiver);
  const [subject, setSubject] = useState(item.subject);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(item.message);
  const [tab, setTab] = useState(
    timezones.find((each) => each.id == item.use_local_timezone)
  );
  const [localTimezone, setLocalTimezone] = useState(null);
  const [status, setStatus] = useState(item.status);
  const [error, setError] = useState(null);

  const context = useContext(UserContext);
  const isActive = item.status == 1;

  const isRequiredFields = () => {
    return (
      receiver &&
      subject &&
      message &&
      tab &&
      (tab?.id == 1 ? localTimezone : true)
    );
  };

  const isEmailValid = () => {
    return validateEmail(email);
  };

  const handleUpdate = async (status) => {
    setLoading(true);

    setError(null);

    const params = {
      receiver: receiver,
      subject: subject,
      message: message,
      status: status,
      timezone: tab?.id,
    };

    const response = await updateReportSchedule(item.id, params);

    Logger("Report Schedule Patch", response);

    if (response.ok) {
      if (onUpdate) {
        onUpdate();
      }
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  const emailsToString = (list) => {
    let data = "";
    list?.map((each) => {
      data += `${each},`;
    });

    if (data) {
      data = data.substring(0, data.length - 1);
    }

    return data;
  };

  const handleEmailAdd = () => {
    const emails = receiver?.split(",");

    if (!emails?.find((each) => each == email)) {
      emails.push(email);

      const data = emailsToString(emails);
      setEmail("");
      setReceiver(data);
    }
  };

  const handleTimezoneFetch = async () => {
    setLoading(true);

    const response = await fetchLocalTimezone();

    Logger("Local Timezone response", response);

    if (response.ok) {
      setLocalTimezone(response.data);
    }
    setLoading(false);
  };

  const renderReceiverRow = (item, index) => {
    return (
      <NtEmailItem
        containerStyle={{ marginRight: 6, marginBottom: 6 }}
        key={item}
        email={item}
        onDelete={() => {
          let emails = receiver?.split(",");
          emails = emails?.filter((each) => each != item);
          const data = emailsToString(emails);
          setReceiver(data);
        }}
      />
    );
  };

  const renderReceivers = (containerStyle) => {
    return (
      <View style={containerStyle}>
        <NtText
          style={{
            marginLeft: 4,
            color: colors.lighGray,
            fontWeight: "700",
            fontStyle: "italic",
            fontSize: 13,
          }}
        >
          Receiver list
        </NtText>
        {receiver?.includes("@") && (
          <NtText style={[{ marginTop: 6 }]}>
            {receiver
              ?.split(",")
              ?.map((each, index) => renderReceiverRow(each, index))}
          </NtText>
        )}
      </View>
    );
  };

  const renderAdd = (containerStyle) => {
    return (
      <NtTouchableEffect
        style={[
          {
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            borderRadius: 15,
            backgroundColor: isEmailValid() ? colors.blue : colors.lighGray,
          },
          containerStyle,
        ]}
        onPress={
          isEmailValid()
            ? () => {
                handleEmailAdd();
              }
            : null
        }
      >
        <NtMaterialIcon name="plus" color="white" />
      </NtTouchableEffect>
    );
  };

  const renderTimezone = (containerStyle) => {
    return (
      <View style={containerStyle}>
        <NtText style={[styles.fieldTitle, { marginLeft: 4 }]}>
          Run timezone *
        </NtText>

        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <NtTabController
            containerStyle={{ marginTop: 4 }}
            options={timezones}
            selection={tab}
            onPress={(item) => {
              setTab(item);
            }}
          />

          {tab?.id == 1 && (
            <View
              style={{
                marginLeft: 5,
                paddingLeft: 8,
                paddingRight: 8,
                paddingTop: 3,
                paddingBottom: 3,
                borderRadius: 20,
                backgroundColor: colors.lighestGray,
              }}
            >
              <NtText
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: localTimezone ? colors.almostBlack : "red",
                }}
              >
                {localTimezone
                  ? localTimezone?.utc_offset
                  : "Local Timezone not set"}
              </NtText>
            </View>
          )}
        </View>
      </View>
    );
  };

  useEffect(() => {
    handleTimezoneFetch();
  }, []);

  return (
    <View style={styles.container}>
      <NtText style={styles.title}>Update Schedule</NtText>
      <NtText style={[styles.subtitle, { marginTop: 6 }]}>
        This update will re-enable the schedule.
      </NtText>

      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Receiver *"}
        placeholder={"Add New Receiver"}
        value={email}
        setValue={(text) => setEmail(text)}
        renderAction={renderAdd({ marginRight: -5 })}
      />

      {renderReceivers({ marginTop: 10, marginLeft: 10 })}

      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Subject *"}
        placeholder={"Subject"}
        value={subject}
        setValue={(text) => setSubject(text)}
      />

      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Message *"}
        placeholder={"Message"}
        value={message}
        setValue={(text) => setMessage(text)}
      />

      {renderTimezone({ marginTop: 15, marginLeft: 4 })}

      <NtModalError error={error} containerStyle={{ marginTop: 6 }} />

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: isActive ? "space-between" : "flex-end",
          marginTop: 25,
        }}
      >
        {isActive && (
          <NtModalActiveButton
            containerStyle={{ marginRight: 15, backgroundColor: "red" }}
            title={"Deactivate"}
            onPress={() => {
              setStatus(0);
              handleUpdate(0);
            }}
            enabled={true}
            icon={"delete"}
          />
        )}

        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}

          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />

          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={isActive ? "Update" : "Update & Activate"}
            onPress={() => {
              if (isRequiredFields()) {
                setStatus(1);
                handleUpdate(1);
              }
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 500,
    padding: 15,
  },
  title: {
    color: colors.darkest,
    fontSize: 15,
    fontWeight: "600",
  },
  subtitle: {
    color: colors.darker,
    fontSize: 14,
    fontWeight: "500",
  },
  fieldTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: colors.darker,
  },
});

export default NtReportScheduleUpdateForm;
